/** ONE RELATIONSHIPS — sections. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileDown, CheckCircle2, MapPin, Gift, Upload, Mail, Tag } from "lucide-react";
import {
  PersonDialog, HouseholdDialog, OrgDialog, AddressDialog, CampaignDialog,
  RecipientDialog, GiftDialog, GroupDialog,
} from "./OneRelationshipsDialogs";

export const SCOPE_BADGE = { BUSINESS: "bg-blue-100 text-blue-900",
  PERSONAL: "bg-purple-100 text-purple-900", SHARED: "bg-teal-100 text-teal-900" };
const DISPATCH_BADGE = { DELIVERED: "bg-emerald-100 text-emerald-800",
  DISPATCHED: "bg-sky-100 text-sky-800", IN_TRANSIT: "bg-sky-100 text-sky-800",
  FAILED: "bg-rose-100 text-rose-800", RETURNED: "bg-rose-100 text-rose-800",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800" };
export const Badge = ({ v, map = SCOPE_BADGE }) => v ? (
  <span className={"text-[9px] font-semibold px-1.5 py-0.5 rounded " + (map[v] || "bg-slate-100 text-slate-500")}>
    {String(v).replace(/_/g, " ")}</span>) : null;
const Card = ({ title, children, action, testid }) => (
  <div className="border border-slate-200 rounded-xl bg-white p-4" data-testid={testid}>
    <div className="flex items-center mb-3">
      <div className="text-[11px] font-heading font-semibold flex-1">{title}</div>{action}
    </div>{children}
  </div>);
const btnCls = "text-[10.5px] px-2 py-1.5 rounded-md border border-slate-300 inline-flex items-center gap-1";
const searchCls = "text-[11px] border border-slate-200 rounded-md px-2 py-1.5 w-56";

function useList(path, refreshKey, q) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get(path, { params: q ? { q } : {} })
        .then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [path, refreshKey, q]);
  return items;
}

async function del(path, id, bump) {
  if (!window.confirm("Delete this record?")) return;
  try { await api.delete(`${path}/${id}`); toast.success("Deleted"); bump(); }
  catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
}

function EntityList({ title, path, refreshKey, bump, access, Dialog, render, testid, addLabel, extra }) {
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const items = useList(path, refreshKey, q);
  return (
    <Card title={title} testid={testid} action={
      <div className="flex items-center gap-2">
        {extra}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            data-testid={`${testid}-search`} className={searchCls} />
        <button onClick={() => setEdit({})} data-testid={`${testid}-add`} className={btnCls}>
          <Plus className="h-3 w-3" /> {addLabel}</button>
      </div>}>
      <div className="space-y-1.5">
        {items.length === 0 && <div className="text-[11px] text-slate-400">Nothing here yet.</div>}
        {items.map((it) => (
          <div key={it.id} className="border border-slate-200 rounded-lg px-3 py-2 flex items-start gap-2"
              data-testid={`${testid}-row-${it.id}`}>
            <div className="flex-1 min-w-0">{render(it)}</div>
            <Badge v={it.scope} />
            <button onClick={() => setEdit(it)} className="p-1 text-slate-400 hover:text-slate-700"
                data-testid={`${testid}-edit-${it.id}`}><Pencil className="h-3 w-3" /></button>
            <button onClick={() => del(path, it.id, bump)} className="p-1 text-slate-400 hover:text-rose-600"
                data-testid={`${testid}-del-${it.id}`}><Trash2 className="h-3 w-3" /></button>
          </div>))}
      </div>
      {edit && <Dialog item={edit} access={access} onClose={() => setEdit(null)}
          onDone={() => { setEdit(null); bump(); }} />}
    </Card>
  );
}

// ------------------------------------------------------------------ OVERVIEW
export function Overview({ refreshKey, goto }) {
  const [d, setD] = useState(null);
  const [occ, setOcc] = useState([]);
  useEffect(() => {
    api.get("/relationships/overview").then(({ data }) => setD(data));
    api.get("/relationships/occasions/upcoming", { params: { days: 30 } })
      .then(({ data }) => setOcc(data.items || []));
  }, [refreshKey]);
  if (!d) return null;
  return (
    <div className="space-y-4" data-testid="rel-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {d.metrics.map((m) => (
          <div key={m.label} className="border border-slate-200 rounded-xl bg-white p-4">
            <div className="text-2xl font-heading font-semibold">{m.value}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{m.label}</div>
          </div>))}
      </div>
      <Card title="Coming up in the next 30 days" action={
        <button onClick={() => goto("OCCASIONS")} className={btnCls}>View all occasions</button>}>
        <div className="space-y-1.5" data-testid="rel-overview-occasions">
          {occ.length === 0 && <div className="text-[11px] text-slate-400">No occasions in the next 30 days.</div>}
          {occ.slice(0, 8).map((o, i) => (
            <div key={i} className="flex items-center gap-2 text-[11.5px] border-b border-slate-50 pb-1">
              <Badge v={o.type} map={{}} />
              <span className="flex-1">{o.name}</span>
              <span className="text-slate-500">{o.date}</span>
              <span className="text-[10px] font-mono text-rose-700">{o.days}d</span>
            </div>))}
        </div>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------ DIRECTORY
export function DirectorySection({ refreshKey }) {
  const [q, setQ] = useState("");
  const items = useList("/relationships/directory", refreshKey, q);
  return (
    <Card title="Unified directory — people, households, clubs, plus Customer & Vendor masters (referenced, never duplicated)"
        testid="rel-directory" action={
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search everything…"
              data-testid="rel-directory-search" className={searchCls} />}>
      <div className="space-y-1">
        {items.length === 0 && <div className="text-[11px] text-slate-400">No entries.</div>}
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 text-[11.5px] border-b border-slate-50 py-1.5">
            <span className={"text-[9px] font-semibold px-1.5 py-0.5 rounded " +
                (it.master_ref ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600")}>
              {it.type}{it.master_ref ? " · MASTER" : ""}</span>
            <span className="flex-1">{it.name}</span>
            <span className="text-[10px] text-slate-400">{(it.detail || "").toString().replace(/_/g, " ")}</span>
            <Badge v={it.scope} />
          </div>))}
      </div>
    </Card>
  );
}

// ------------------------------------------------------------------ PEOPLE
export function PeopleSection(props) {
  const fileRef = { current: null };
  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/relationships/import", fd);
      toast.success(`Imported: ${data.created_people} people, ${data.created_addresses} addresses (${data.skipped} skipped)`);
      props.bump();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    e.target.value = "";
  };
  const template = async () => {
    const res = await api.get("/relationships/import/template", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "relationships-import.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  return <EntityList {...props} title="People — friends, family & contacts" addLabel="Add person"
      path="/relationships/people" Dialog={PersonDialog} testid="rel-people"
      extra={
        <>
          <button onClick={template} data-testid="rel-import-template" className={btnCls}>
            <FileDown className="h-3 w-3" /> Template</button>
          <label className={btnCls + " cursor-pointer"} data-testid="rel-import-csv">
            <Upload className="h-3 w-3" /> Import CSV
            <input type="file" accept=".csv" className="hidden" onChange={importCsv}
                ref={(el) => (fileRef.current = el)} />
          </label>
        </>}
      render={(p) => (
        <>
          <div className="text-[11.5px] font-medium">{p.display_name}
            <span className="text-slate-400 font-normal"> · {(p.relationship_category || "").replace(/_/g, " ")}</span></div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {p.mobile && <span>{p.mobile} · </span>}{p.email && <span>{p.email} · </span>}
            {p.company && <span>{p.company} · </span>}{p.city && <span>{p.city} · </span>}
            {p.birthday && <span>🎂 {p.birthday} · </span>}{p.anniversary && <span>💍 {p.anniversary}</span>}
          </div>
        </>)} />;
}

// ------------------------------------------------------------------ HOUSEHOLDS
export function HouseholdsSection(props) {
  const people = useList("/relationships/people", props.refreshKey);
  return <EntityList {...props} title="Households — one gift per household, never duplicates" addLabel="Add household"
      path="/relationships/households" Dialog={HouseholdDialog} testid="rel-households"
      render={(h) => {
        const members = people.filter((p) => p.household_id === h.id);
        return (
          <>
            <div className="text-[11.5px] font-medium">{h.display_name}
              {h.city && <span className="text-slate-400 font-normal"> · {h.city}</span>}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              {h.preferred_gift_recipient && <span><b>Gift to:</b> {h.preferred_gift_recipient} · </span>}
              <b>Members:</b> {members.length ? members.map((m) => m.display_name).join(", ") : "none linked yet"}
            </div>
          </>);
      }} />;
}

// ------------------------------------------------------------------ ORGS
export function OrgsSection(props) {
  return <EntityList {...props} title="Clubs, trade & business associations — our memberships" addLabel="Add club / association"
      path="/relationships/orgs" Dialog={OrgDialog} testid="rel-orgs"
      render={(o) => (
        <>
          <div className="text-[11.5px] font-medium">{o.name}
            <span className="text-slate-400 font-normal"> · {(o.org_type || "").replace(/_/g, " ")}</span></div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {o.chapter && <span>{o.chapter} · </span>}
            {o.membership_number && <span>Membership #{o.membership_number} · </span>}
            {o.renewal_date && <span className="text-amber-700 font-medium">Renewal: {o.renewal_date} · </span>}
            {o.contact_person && <span>{o.contact_person} </span>}{o.contact_mobile && <span>({o.contact_mobile})</span>}
          </div>
        </>)} />;
}

// ------------------------------------------------------------------ ADDRESSES
export function AddressBook({ refreshKey, bump, access }) {
  const [edit, setEdit] = useState(null);
  const items = useList("/relationships/addresses", refreshKey);
  const act = async (aid, ep, msg) => {
    try { await api.post(`/relationships/addresses/${aid}/${ep}`); toast.success(msg); bump(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Card title="Address book — full history kept, old addresses are never overwritten" testid="rel-addresses"
        action={<button onClick={() => setEdit({})} data-testid="rel-addr-add" className={btnCls}>
          <Plus className="h-3 w-3" /> Add address</button>}>
      <div className="space-y-1.5">
        {items.length === 0 && <div className="text-[11px] text-slate-400">No addresses yet.</div>}
        {items.map((a) => (
          <div key={a.id} className={"border rounded-lg px-3 py-2 flex items-start gap-2 " +
              (a.current ? "border-slate-200" : "border-slate-100 opacity-60")}
              data-testid={`rel-addr-row-${a.id}`}>
            <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[11.5px] font-medium">{a.recipient_name || a.entity_name}
                <span className="text-slate-400 font-normal"> · {(a.address_type || "").replace(/_/g, " ")}</span>
                {!a.current && <span className="text-[9px] text-slate-400 ml-1">(HISTORY)</span>}
                {a.confirmation_recommended && <span className="text-[9px] font-semibold text-amber-700 ml-1">CONFIRMATION RECOMMENDED</span>}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {[a.line1, a.line2, a.city, a.state, a.postal_code].filter(Boolean).join(", ")}
                {a.mobile && <span> · {a.mobile}</span>}
                {a.last_confirmed && <span> · confirmed {String(a.last_confirmed).slice(0, 10)}</span>}
              </div>
            </div>
            <Badge v={a.scope} />
            {a.current
              ? <button onClick={() => act(a.id, "confirm", "Address confirmed")} className={btnCls}
                    data-testid={`rel-addr-confirm-${a.id}`}><CheckCircle2 className="h-3 w-3" /> Confirm</button>
              : <button onClick={() => act(a.id, "mark-current", "Marked current")} className={btnCls}
                    data-testid={`rel-addr-current-${a.id}`}>Mark current</button>}
            <button onClick={() => setEdit(a)} className="p-1 text-slate-400 hover:text-slate-700"
                data-testid={`rel-addr-edit-${a.id}`}><Pencil className="h-3 w-3" /></button>
            <button onClick={() => del("/relationships/addresses", a.id, bump)}
                className="p-1 text-slate-400 hover:text-rose-600"
                data-testid={`rel-addr-del-${a.id}`}><Trash2 className="h-3 w-3" /></button>
          </div>))}
      </div>
      {edit && <AddressDialog access={access} item={edit.id ? edit : null}
          onClose={() => setEdit(null)} onDone={() => { setEdit(null); bump(); }} />}
    </Card>
  );
}

// ------------------------------------------------------------------ OCCASIONS
export function OccasionsSection({ refreshKey }) {
  const [days, setDays] = useState(90);
  const [items, setItems] = useState([]);
  const digest = async () => {
    try {
      const { data } = await api.post("/relationships/reminders/run");
      toast.success(`Digest ${data.status === "LOGGED" ? "generated" : "emailed"} — a monthly copy also goes out automatically on the 1st`);
    } catch (e) { toast.error(e.response?.data?.detail || "Digest failed"); }
  };
  useEffect(() => {
    api.get("/relationships/occasions/upcoming", { params: { days } })
      .then(({ data }) => setItems(data.items || []));
  }, [refreshKey, days]);
  return (
    <Card title="Upcoming occasions — birthdays, anniversaries & membership renewals" testid="rel-occasions"
        action={<div className="flex items-center gap-2">
          <button onClick={digest} data-testid="rel-occ-digest" className={btnCls}>
            <Mail className="h-3 w-3" /> Email me this digest</button>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            data-testid="rel-occ-window" className="text-[11px] border border-slate-200 rounded-md px-2 py-1.5">
          {[30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>Next {d} days</option>)}
        </select></div>}>
      <div className="space-y-1.5">
        {items.length === 0 && <div className="text-[11px] text-slate-400">
          No occasions in this window. Add birthdays / anniversaries on People, renewal dates on Clubs &amp; Associations.</div>}
        {items.map((o, i) => (
          <div key={i} className="flex items-center gap-2 text-[11.5px] border-b border-slate-50 pb-1.5">
            <Badge v={o.type} map={{ BIRTHDAY: "bg-pink-100 text-pink-800",
              ANNIVERSARY: "bg-purple-100 text-purple-800",
              MEMBERSHIP_RENEWAL: "bg-amber-100 text-amber-800" }} />
            <span className="flex-1">{o.name}</span>
            <Badge v={o.scope} />
            <span className="text-slate-500">{o.date}</span>
            <span className={"text-[10px] font-mono w-10 text-right " + (o.days <= 7 ? "text-rose-700 font-semibold" : "text-slate-500")}>
              {o.days}d</span>
          </div>))}
      </div>
    </Card>
  );
}

// ------------------------------------------------------------------ CAMPAIGNS
export function CampaignsSection({ refreshKey, bump, access }) {
  const [edit, setEdit] = useState(null);
  const [openId, setOpenId] = useState(null);
  const items = useList("/relationships/campaigns", refreshKey);
  const open = items.find((c) => c.id === openId);
  if (open) return <CampaignDetail campaign={open} access={access} bump={bump}
      onBack={() => { setOpenId(null); bump(); }} />;
  return (
    <Card title="Gift campaigns — Diwali, New Year, milestones" testid="rel-campaigns"
        action={<button onClick={() => setEdit({})} data-testid="rel-camp-add" className={btnCls}>
          <Plus className="h-3 w-3" /> New campaign</button>}>
      <div className="grid md:grid-cols-2 gap-2">
        {items.length === 0 && <div className="text-[11px] text-slate-400">No campaigns yet.</div>}
        {items.map((c) => (
          <button key={c.id} onClick={() => setOpenId(c.id)} data-testid={`rel-camp-open-${c.id}`}
              className="border border-slate-200 rounded-lg p-3 text-left hover:border-rose-600 transition-colors">
            <div className="flex items-center gap-2">
              <Gift className="h-3.5 w-3.5 text-rose-700" />
              <div className="text-[12px] font-medium flex-1">{c.name}</div>
              <Badge v={c.status} map={{ PLANNING: "bg-slate-100 text-slate-600",
                READY: "bg-sky-100 text-sky-800", DISPATCH_IN_PROGRESS: "bg-amber-100 text-amber-800",
                COMPLETED: "bg-emerald-100 text-emerald-800" }} />
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              {(c.occasion || "").replace(/_/g, " ")} {c.year} {c.gift && <span>· default gift: {c.gift}</span>}
            </div>
          </button>))}
      </div>
      {edit && <CampaignDialog item={edit} access={access} onClose={() => setEdit(null)}
          onDone={() => { setEdit(null); bump(); }} />}
    </Card>
  );
}

function CampaignDetail({ campaign, access, bump, onBack }) {
  const [rk, setRk] = useState(0);
  const [rows, setRows] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [add, setAdd] = useState(false);
  const [edit, setEdit] = useState(false);
  const [labelQ, setLabelQ] = useState("");
  const addByLabel = async () => {
    if (!labelQ.trim()) return toast.error("Type a label name");
    try {
      const { data } = await api.post(`/relationships/campaigns/${campaign.id}/add-by-label`,
        { label: labelQ.trim() });
      toast.success(`${data.added} added from "${data.label}" · ${data.skipped_household_duplicates} household dupes skipped · ${data.already_in_campaign} already in`);
      setLabelQ(""); setRk((k) => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => {
    api.get(`/relationships/campaigns/${campaign.id}/recipients`).then(({ data }) => {
      setRows(data.items || []); setStatuses(data.dispatch_statuses || []);
    });
  }, [campaign.id, rk]);
  const patch = async (rid, body) => {
    try { await api.patch(`/relationships/recipients/${rid}`, body); setRk((k) => k + 1); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const removeRecipient = async (rid) => {
    if (!window.confirm("Remove this recipient from the campaign?")) return;
    try { await api.delete(`/relationships/recipients/${rid}`); toast.success("Recipient removed"); setRk((k) => k + 1); }
    catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };
  const removeCampaign = async () => {
    if (!window.confirm("Delete this campaign and all its recipients?")) return;
    try { await api.delete(`/relationships/campaigns/${campaign.id}`); toast.success("Campaign deleted"); onBack(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const labels = async (fmt) => {
    try {
      const res = await api.get(`/relationships/campaigns/${campaign.id}/labels`,
        { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `labels.${fmt}`; a.click();
      URL.revokeObjectURL(url); setRk((k) => k + 1);
    } catch { toast.error("Label generation failed"); }
  };
  return (
    <Card testid="rel-camp-detail"
        title={`${campaign.name} — ${(campaign.occasion || "").replace(/_/g, " ")} ${campaign.year || ""} · ${rows.length} recipient(s)`}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Tag className="h-3 w-3 text-slate-400" />
              <input value={labelQ} onChange={(e) => setLabelQ(e.target.value)}
                  placeholder="Label…" data-testid="rel-camp-label-input"
                  className="text-[10.5px] border border-slate-200 rounded-md px-2 py-1.5 w-24" />
              <button onClick={addByLabel} data-testid="rel-camp-add-by-label"
                  className={btnCls}>Add by label</button>
            </span>
            {["csv", "xlsx", "pdf"].map((f) => (
              <button key={f} onClick={() => labels(f)} data-testid={`rel-camp-labels-${f}`}
                  className={btnCls}><FileDown className="h-3 w-3" /> {f.toUpperCase()}</button>))}
            <button onClick={() => setEdit(true)} data-testid="rel-camp-edit" className={btnCls}>
              <Pencil className="h-3 w-3" /> Edit</button>
            <button onClick={removeCampaign} data-testid="rel-camp-delete"
                className="text-[10.5px] px-2 py-1.5 rounded-md border border-rose-200 text-rose-700 inline-flex items-center gap-1">
              <Trash2 className="h-3 w-3" /> Delete</button>
            <button onClick={() => setAdd(true)} data-testid="rel-camp-add-recipient"
                className="text-[10.5px] px-2 py-1.5 rounded-md bg-rose-950 text-white inline-flex items-center gap-1">
              <Plus className="h-3 w-3" /> Add recipient</button>
            <button onClick={onBack} data-testid="rel-camp-back" className={btnCls}>Back</button>
          </div>}>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead><tr className="text-left text-[9.5px] uppercase text-slate-400 border-b border-slate-100">
            <th className="py-1.5 pr-2">Recipient</th><th className="pr-2">Gift</th>
            <th className="pr-2">Address</th><th className="pr-2">Duplicate</th>
            <th className="pr-2">Approval</th><th className="pr-2">Dispatch</th><th></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="py-3 text-slate-400">No recipients yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-50" data-testid={`rel-rec-row-${r.id}`}>
                <td className="py-1.5 pr-2">
                  <div className="font-medium">{r.recipient_name}</div>
                  <div className="text-[9.5px] text-slate-400">{r.household_name || r.mobile || ""}</div></td>
                <td className="pr-2">{r.gift || campaign.gift || "—"}{r.quantity ? ` ×${r.quantity}` : ""}</td>
                <td className="pr-2 max-w-[200px]">
                  <div className="truncate">{[r.address, r.city].filter(Boolean).join(", ") || "—"}</div></td>
                <td className="pr-2"><Badge v={r.duplicate_status}
                    map={{ UNIQUE: "bg-emerald-100 text-emerald-800",
                      POSSIBLE_DUPLICATE: "bg-amber-100 text-amber-800",
                      ALREADY_INCLUDED: "bg-rose-100 text-rose-800" }} /></td>
                <td className="pr-2">
                  {r.approval_status === "APPROVED" ? <Badge v="APPROVED" map={{ APPROVED: "bg-emerald-100 text-emerald-800" }} />
                    : <button onClick={() => patch(r.id, { approval_status: "APPROVED" })}
                        data-testid={`rel-rec-approve-${r.id}`} className={btnCls}>Approve</button>}</td>
                <td className="pr-2">
                  <select value={r.dispatch_status} data-testid={`rel-rec-dispatch-${r.id}`}
                      onChange={(e) => patch(r.id, { dispatch_status: e.target.value })}
                      className={"text-[10px] border border-slate-200 rounded px-1.5 py-1 " +
                        (DISPATCH_BADGE[r.dispatch_status] || "")}>
                    {statuses.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select></td>
                <td>
                  <button onClick={() => removeRecipient(r.id)} data-testid={`rel-rec-del-${r.id}`}
                      className="p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-3 w-3" /></button></td>
              </tr>))}
          </tbody>
        </table>
      </div>
      {add && <RecipientDialog campaign={campaign} access={access} onClose={() => setAdd(false)}
          onDone={() => { setAdd(false); setRk((k) => k + 1); }} />}
      {edit && <CampaignDialog item={campaign} access={access} onClose={() => setEdit(false)}
          onDone={() => { setEdit(false); bump(); }} />}
    </Card>
  );
}

// ------------------------------------------------------------------ CATALOG / LABELS / HISTORY
export function CatalogSection(props) {
  return <EntityList {...props} title="Gift catalog — standard gifts & budgets" addLabel="Add gift"
      path="/relationships/gifts" Dialog={GiftDialog} testid="rel-gifts"
      render={(g) => (
        <>
          <div className="text-[11.5px] font-medium">{g.name}
            <span className="text-slate-400 font-normal"> · {(g.gift_type || "").replace(/_/g, " ")}</span></div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {g.budget && <span>Budget ₹{g.budget} · </span>}{g.vendor && <span>From: {g.vendor} · </span>}{g.notes}
          </div>
        </>)} />;
}

export function LabelsSection(props) {
  return <EntityList {...props} title="Labels — segment people & households for campaigns" addLabel="Add label"
      path="/relationships/groups" Dialog={GroupDialog} testid="rel-groups"
      render={(g) => (
        <>
          <div className="text-[11.5px] font-medium">{g.name}</div>
          {g.description && <div className="text-[10px] text-slate-500 mt-0.5">{g.description}</div>}
        </>)} />;
}

export function HistorySection({ refreshKey }) {
  const items = useList("/relationships/audit", refreshKey);
  return (
    <Card title="History — every change is recorded" testid="rel-history">
      <div className="space-y-1">
        {items.map((a) => (
          <div key={a.id} className="flex items-center gap-2 text-[10.5px] border-b border-slate-50 py-1">
            <span className="font-mono text-slate-400 w-36 shrink-0">{String(a.at).slice(0, 16).replace("T", " ")}</span>
            <span className="font-semibold text-slate-600 w-44 shrink-0">{(a.action || "").replace(/_/g, " ")}</span>
            <span className="flex-1 truncate text-slate-500">{a.detail || ""}</span>
            <span className="text-slate-400">{a.actor}</span>
          </div>))}
      </div>
    </Card>
  );
}
